import { CommonModule } from "@angular/common";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { TranslocoDirective } from "@jsverse/transloco";
import { FieldType, FieldTypeConfig } from "@ngx-formly/core";
import { Subscription } from "rxjs";
import { langCodes } from "../../../../../domain/languages";

interface LangFormControls {
  fi: FormControl<string | null>;
  sv: FormControl<string | null>;
  en: FormControl<string | null>;
  no: FormControl<string | null>;
}

/* 
  Example usage:
  {
    key: "name",
    type: "textTranslatable",
    props: {
      label: {
        en: "Name",
        fi: "Nimi",
        sv: "Namn",
      },                                          // Optional
      description: {
        en: "Enter the name in all languages",
        fi: "Syötä nimi kaikilla kielillä",
        sv: "Ange namnet på alla språk",
      },                                          // Optional
      required: false,
    },
  },
*/

@Component({
  selector: "app-text-translatable",
  templateUrl: "./text-translatable.type.html",
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslocoDirective],
})
export class TextTranslatableComponent extends FieldType<FieldTypeConfig> implements OnInit, OnDestroy {
  readonly languages = langCodes;
  langFormGroup!: FormGroup;
  private subscription?: Subscription;

  ngOnInit(): void {
    const required = !!this.props.required;
    const validator = required ? Validators.required : null;

    const initialValue = this.formControl.value ?? { fi: "", sv: "", en: "", no: "" };
    this.formControl.setValue(initialValue, { emitEvent: false });

    this.langFormGroup = new FormGroup({
      fi: new FormControl(initialValue.fi, validator),
      sv: new FormControl(initialValue.sv, validator),
      en: new FormControl(initialValue.en, validator),
      no: new FormControl(initialValue.no, validator),
    } satisfies LangFormControls);

    if (this.langFormGroup.invalid) {
      this.formControl.setErrors({ invalid: true });
    }

    this.subscription = this.langFormGroup.valueChanges.subscribe((val) => {
      this.formControl.setValue(val, { emitEvent: false });
      this.formControl.markAsDirty();

      if (this.langFormGroup.valid) {
        this.formControl.setErrors(null);
      } else {
        this.formControl.setErrors({ invalid: true });
      }
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  public onBlur(lang: keyof LangFormControls): void {
    this.langFormGroup.get(lang)?.markAsTouched();
    this.formControl.markAsTouched();
  }

  public isFieldInvalid(lang: keyof LangFormControls): boolean {
    const control = this.langFormGroup.get(lang);
    return !!control && control.invalid && (control.dirty || control.touched);
  }
}
